import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar as CalendarIcon, AlertCircle, ChevronLeft, ChevronRight, Check } from 'lucide-react';
interface ExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEndDate: string;
}
// Mock bookings for availability display
const MOCK_BOOKINGS = [{
  start: '2024-01-15',
  end: '2024-01-29'
}, {
  start: '2024-02-05',
  end: '2024-02-19'
}];
export function ExtensionModal({
  isOpen,
  onClose,
  currentEndDate
}: ExtensionModalProps) {
  const [newEndDate, setNewEndDate] = useState('');
  const [error, setError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  if (!isOpen) return null;
  const handleDateSelect = (dateStr: string) => {
    const currentEnd = new Date(currentEndDate);
    const newEnd = new Date(dateStr);
    const daysDiff = Math.ceil((newEnd.getTime() - currentEnd.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff < 1) {
      setError('Extension date must be after current end date');
      return;
    }
    if (daysDiff > 14) {
      setError('Extension cannot exceed 14 days');
      return;
    }
    // Check if extension conflicts with existing bookings
    const hasConflict = MOCK_BOOKINGS.some(booking => {
      const bookingStart = new Date(booking.start);
      return bookingStart > currentEnd && bookingStart <= newEnd;
    });
    if (hasConflict) {
      setError('Extension conflicts with an existing booking');
      return;
    }
    setError('');
    setNewEndDate(dateStr);
  };
  const handleConfirm = () => {
    if (newEndDate) {
      // Handle booking extension
      onClose();
      setTimeout(() => {
        setNewEndDate('');
        setError('');
      }, 300);
    }
  };
  const isDateBooked = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return MOCK_BOOKINGS.some(booking => {
      return dateStr >= booking.start && dateStr <= booking.end;
    });
  };
  const isDateInExtensionRange = (date: Date) => {
    if (!newEndDate) return false;
    const dateStr = date.toISOString().split('T')[0];
    return dateStr > currentEndDate && dateStr <= newEndDate;
  };
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };
  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };
  const formatMonthYear = () => {
    return currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const calculateExtensionDays = () => {
    if (!newEndDate) return 0;
    const currentEnd = new Date(currentEndDate);
    const newEnd = new Date(newEndDate);
    return Math.ceil((newEnd.getTime() - currentEnd.getTime()) / (1000 * 60 * 60 * 24));
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-206">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-207" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.2
    }} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-208">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white" data-id="element-209">
          <div data-id="element-210">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-211">Extend Booking</h2>
            <p className="text-xs text-slate-500" data-id="element-212">
              Current end date: {formatDate(currentEndDate)}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors" data-id="element-213">
            <X size={20} data-id="element-214" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-215">
          <div className="space-y-6" data-id="element-216">
            {/* Calendar View */}
            <div data-id="element-217">
              <div className="flex items-center justify-between mb-4" data-id="element-218">
                <h3 className="text-sm font-semibold text-slate-700" data-id="element-219">
                  Select New End Date
                </h3>
                <div className="flex items-center gap-2" data-id="element-220">
                  <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-221">
                    <ChevronLeft size={18} className="text-slate-600" data-id="element-222" />
                  </button>
                  <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center" data-id="element-223">
                    {formatMonthYear()}
                  </span>
                  <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-224">
                    <ChevronRight size={18} className="text-slate-600" data-id="element-225" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2" data-id="element-226">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="text-slate-500 font-medium py-2" data-id="element-227">
                    {d}
                  </div>)}
              </div>

              <div className="grid grid-cols-7 gap-1" data-id="element-228">
                {getDaysInMonth().map((date, idx) => {
                if (!date) {
                  return <div key={`empty-${idx}`} className="aspect-square" data-id="element-229" />;
                }
                const isBooked = isDateBooked(date);
                const dateStr = date.toISOString().split('T')[0];
                const isSelected = dateStr === newEndDate;
                const inExtensionRange = isDateInExtensionRange(date);
                const isPast = date <= new Date(currentEndDate);
                const isCurrentEnd = dateStr === currentEndDate;
                // Check if date is beyond 2 weeks from current end
                const currentEnd = new Date(currentEndDate);
                const maxExtension = new Date(currentEnd.getTime() + 14 * 24 * 60 * 60 * 1000);
                const isBeyondMaxExtension = date > maxExtension;
                const isDisabled = isBooked || isPast || isBeyondMaxExtension;
                return <button key={idx} onClick={() => !isDisabled && handleDateSelect(dateStr)} disabled={isDisabled} className={`
                        aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all duration-200
                        ${isCurrentEnd ? 'bg-slate-300 text-slate-700 ring-2 ring-slate-400' : isDisabled ? 'bg-red-100 text-red-400 cursor-not-allowed' : isSelected ? 'bg-slate-900 text-white shadow-md scale-105' : inExtensionRange ? 'bg-slate-200 text-slate-700' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 hover:scale-105'}
                      `} data-id="element-230">
                      {date.getDate()}
                    </button>;
              })}
              </div>

              <div className="flex items-center gap-4 mt-3 text-xs" data-id="element-231">
                <div className="flex items-center gap-1.5" data-id="element-232">
                  <div className="w-3 h-3 bg-slate-300 rounded ring-1 ring-slate-400" data-id="element-233"></div>
                  <span className="text-slate-600" data-id="element-234">Current End</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-235">
                  <div className="w-3 h-3 bg-slate-900 rounded" data-id="element-236"></div>
                  <span className="text-slate-600" data-id="element-237">New End</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-238">
                  <div className="w-3 h-3 bg-red-100 rounded" data-id="element-239"></div>
                  <span className="text-slate-600" data-id="element-240">Unavailable</span>
                </div>
              </div>
            </div>

            {/* Date Input */}
            <div data-id="element-241">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-242">
                New End Date
              </label>
              <input type="date" value={newEndDate} min={new Date(new Date(currentEndDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]} max={new Date(new Date(currentEndDate).getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]} onChange={e => handleDateSelect(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent text-slate-900 text-sm" data-id="element-243" />
            </div>

            {newEndDate && !error && <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-sm text-emerald-700" data-id="element-244">
                <p className="font-medium" data-id="element-245">
                  Extension: {calculateExtensionDays()} days
                </p>
                <p className="opacity-80 mt-1" data-id="element-246">
                  New end date: {formatDate(newEndDate)}
                </p>
              </div>}

            {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex gap-2 items-start" data-id="element-247">
                <AlertCircle size={18} className="mt-0.5 shrink-0" data-id="element-248" />
                <span data-id="element-249">{error}</span>
              </div>}

            <div className="bg-blue-50 text-blue-700 p-4 rounded-lg text-sm flex gap-3 items-start" data-id="element-250">
              <CalendarIcon size={18} className="mt-0.5 shrink-0" data-id="element-251" />
              <div data-id="element-252">
                <p className="font-medium" data-id="element-253">Extension Limit</p>
                <p className="opacity-80 mt-1" data-id="element-254">
                  Extensions are limited to 14 days and must not conflict with
                  existing bookings.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center" data-id="element-255">
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors" data-id="element-256">
            Cancel
          </button>

          <button onClick={handleConfirm} disabled={!newEndDate || !!error} className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold shadow-sm transition-all duration-200
              ${!newEndDate || !!error ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md hover:-translate-y-0.5'}
            `} data-id="element-257">
            Confirm Extension
            <Check size={16} data-id="element-258" />
          </button>
        </div>
      </motion.div>
    </div>;
}